<template>
  <div>
    <div
      v-if="!years.length"
      class="text-center p-8 text-neutral-500"
    >
      <p>No transactions found.</p>
    </div>

    <div v-else>
      <div class="mb-8">
        <div class="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <template v-if="hasDefaultCurrency">
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Assets
              </div>
              <div class="text-green-700 dark:text-green-400 text-xl font-semibold">
                {{ formatAmount(overallSummary.assets, currency) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Liabilities
              </div>
              <div class="text-red-700 dark:text-red-400 text-xl font-semibold">
                {{ formatAmount(overallSummary.liabilities, currency) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Net Worth
              </div>
              <div
                :class="overallSummary.netWorth >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'"
                class="text-xl font-semibold"
              >
                {{ formatAmount(overallSummary.netWorth, currency) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Income
              </div>
              <div class="text-green-700 dark:text-green-400 text-xl font-semibold">
                {{ formatAmount(overallSummary.income, currency) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Expenses
              </div>
              <div class="text-red-700 dark:text-red-400 text-xl font-semibold">
                {{ formatAmount(overallSummary.expenses, currency) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Cash Flow
              </div>
              <div
                :class="overallSummary.netCashFlow >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'"
                class="text-xl font-semibold"
              >
                {{ formatAmount(overallSummary.netCashFlow, currency) }}
              </div>
            </div>
          </template>

          <!-- No default currency: one line per currency, no blended Net Worth/Cash Flow -->
          <template v-else>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Assets
              </div>
              <div
                v-for="(amount, unit) in overallSummary.assetsByUnit"
                :key="`asset-${unit}`"
                class="text-green-700 dark:text-green-400 text-xl font-semibold"
              >
                {{ formatAmount(amount, unit) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Liabilities
              </div>
              <div
                v-for="(amount, unit) in overallSummary.liabilitiesByUnit"
                :key="`liability-${unit}`"
                class="text-red-700 dark:text-red-400 text-xl font-semibold"
              >
                {{ formatAmount(amount, unit) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Income
              </div>
              <div
                v-for="(amount, unit) in overallSummary.incomeByUnit"
                :key="`income-${unit}`"
                class="text-green-700 dark:text-green-400 text-xl font-semibold"
              >
                {{ formatAmount(amount, unit) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Expenses
              </div>
              <div
                v-for="(amount, unit) in overallSummary.expensesByUnit"
                :key="`expense-${unit}`"
                class="text-red-700 dark:text-red-400 text-xl font-semibold"
              >
                {{ formatAmount(amount, unit) }}
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Chart: needs a single currency to plot one trend line -->
      <div
        v-if="years.length > 1 && hasDefaultCurrency"
        class="mb-8"
      >
        <div
          ref="chartWrapper"
          class="h-64"
        />
      </div>

      <!-- Per-Year Breakdown -->
      <div class="space-y-6">
        <div
          v-for="yearData in yearSummaries"
          :key="yearData.year"
          class="bg-neutral-100 dark:bg-neutral-900 rounded-lg p-4"
        >
          <h4 class="text-neutral-800 dark:text-neutral-200 font-semibold mb-3">
            {{ yearData.year }}
          </h4>
          <div class="grid grid-cols-2 lg:grid-cols-3 gap-3 text-sm mb-4">
            <template v-if="hasDefaultCurrency">
              <div>
                <span class="text-neutral-500">Assets:</span>
                <a
                  href="javascript:;"
                  class="text-green-700 dark:text-green-400 ml-2 hover:underline"
                  @click="zoomYear(yearData.year, 'Assets:')"
                >{{ formatAmount(yearData.assets, currency) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Liabilities:</span>
                <a
                  href="javascript:;"
                  class="text-red-700 dark:text-red-400 ml-2 hover:underline"
                  @click="zoomYear(yearData.year, 'Liabilities:')"
                >{{ formatAmount(Math.abs(yearData.liabilities), currency) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Net Worth:</span>
                <span
                  :class="yearData.netWorth >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'"
                  class="ml-2"
                >{{ formatAmount(yearData.netWorth, currency) }}</span>
              </div>
              <div>
                <span class="text-neutral-500">Income:</span>
                <a
                  href="javascript:;"
                  class="text-green-700 dark:text-green-400 ml-2 hover:underline"
                  @click="zoomYear(yearData.year, 'Income:')"
                >{{ formatAmount(yearData.income, currency) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Expenses:</span>
                <a
                  href="javascript:;"
                  class="text-red-700 dark:text-red-400 ml-2 hover:underline"
                  @click="zoomYear(yearData.year, 'Expenses:')"
                >{{ formatAmount(yearData.expenses, currency) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Cash Flow:</span>
                <span
                  :class="yearData.netCashFlow >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'"
                  class="ml-2"
                >{{ formatAmount(yearData.netCashFlow, currency) }}</span>
              </div>
            </template>

            <!-- No default currency: one line per currency, no blended Net Worth/Cash Flow -->
            <template v-else>
              <div>
                <span class="text-neutral-500">Assets:</span>
                <a
                  v-for="(amount, unit) in yearData.assetsByUnit"
                  :key="`${yearData.year}-asset-${unit}`"
                  href="javascript:;"
                  class="text-green-700 dark:text-green-400 ml-2 hover:underline"
                  @click="zoomYear(yearData.year, 'Assets:')"
                >{{ formatAmount(amount, unit) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Liabilities:</span>
                <a
                  v-for="(amount, unit) in yearData.liabilitiesByUnit"
                  :key="`${yearData.year}-liability-${unit}`"
                  href="javascript:;"
                  class="text-red-700 dark:text-red-400 ml-2 hover:underline"
                  @click="zoomYear(yearData.year, 'Liabilities:')"
                >{{ formatAmount(Math.abs(amount), unit) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Income:</span>
                <a
                  v-for="(amount, unit) in yearData.incomeByUnit"
                  :key="`${yearData.year}-income-${unit}`"
                  href="javascript:;"
                  class="text-green-700 dark:text-green-400 ml-2 hover:underline"
                  @click="zoomYear(yearData.year, 'Income:')"
                >{{ formatAmount(amount, unit) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Expenses:</span>
                <a
                  v-for="(amount, unit) in yearData.expensesByUnit"
                  :key="`${yearData.year}-expense-${unit}`"
                  href="javascript:;"
                  class="text-red-700 dark:text-red-400 ml-2 hover:underline"
                  @click="zoomYear(yearData.year, 'Expenses:')"
                >{{ formatAmount(amount, unit) }}</a>
              </div>
            </template>
          </div>

          <!-- Category Breakdown -->
          <div
            v-if="yearData.categories.length > 0"
            class="mt-4 border-t border-neutral-300 dark:border-neutral-800 pt-4"
          >
            <table class="w-full text-sm">
              <thead>
                <tr class="text-neutral-500">
                  <th class="text-left py-1">
                    Category
                  </th>
                  <th class="text-right py-1">
                    Income
                  </th>
                  <th class="text-right py-1">
                    Expenses
                  </th>
                  <th class="text-right py-1">
                    Net
                  </th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="category in yearData.categories"
                  :key="`${yearData.year}-${category.name}`"
                >
                  <tr
                    v-for="(net, unit) in category.netByUnit"
                    :key="`${yearData.year}-${category.name}-${unit}`"
                    class="border-t border-neutral-300 dark:border-neutral-800"
                  >
                    <td class="py-1 text-neutral-600 dark:text-neutral-400">
                      {{ category.name }}
                    </td>
                    <td class="py-1 text-right">
                      <a
                        v-if="category.incomeByUnit[unit] > 0"
                        href="javascript:;"
                        class="text-green-700 dark:text-green-400 hover:underline"
                        @click.stop="zoomCategory(yearData.year, category.name, 'Income:')"
                      >{{ formatAmount(category.incomeByUnit[unit], unit) }}</a>
                      <span
                        v-else
                        class="text-neutral-500"
                      >—</span>
                    </td>
                    <td class="py-1 text-right">
                      <a
                        v-if="category.expensesByUnit[unit] > 0"
                        href="javascript:;"
                        class="text-red-700 dark:text-red-400 hover:underline"
                        @click.stop="zoomCategory(yearData.year, category.name, 'Expenses:')"
                      >{{ formatAmount(category.expensesByUnit[unit], unit) }}</a>
                      <span
                        v-else
                        class="text-neutral-500"
                      >—</span>
                    </td>
                    <td
                      class="py-1 text-right"
                      :class="net >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'"
                    >
                      {{ formatAmount(net, unit) }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { formatAmount, convertToCurrency } from '@/parse-and-play/utils'

export default {
  props: {
    app: { type: Object, default: null },
    sheet: { type: Object, default: null },
    transactions: { type: Array, default: () => [] }
  },

  computed: {
    // Empty string is a deliberate "no default currency" choice (see
    // Identity) and must stay empty, not fall back to CAD -- only a
    // missing identity altogether falls back.
    currency () {
      return this.app?.identity ? this.app.identity.defaultCurrency : 'CAD'
    },

    hasDefaultCurrency () {
      return !!this.currency
    },

    // Derive known currencies from transactions - currencies used in Income/Expenses/Liabilities are real money
    knownCurrencies () {
      const currencies = new Set()
      for (const t of this.transactions) {
        // Use original postings to see all account types, not just filtered ones
        const postings = t._original?.getPostings() || t.getPostings()
        for (const p of postings) {
          if (p.account.startsWith('Income:') || p.account.startsWith('Expenses:') || p.account.startsWith('Liabilities:')) {
            if (p.currency) {
              currencies.add(p.currency)
            }
          }
        }
      }
      return currencies
    },

    years () {
      const yearsSet = new Set()
      for (const t of this.transactions) {
        yearsSet.add(Math.floor(t.date / 10000))
      }
      return [...yearsSet].sort()
    },

    yearSummaries () {
      const summaries = []
      let cumulativeAssetsByUnit = {}
      let cumulativeLiabilitiesByUnit = {}
      let cumulativeAssets = 0
      let cumulativeLiabilities = 0

      for (const year of this.years) {
        const summary = this.calculateYearSummary(year, cumulativeAssetsByUnit, cumulativeLiabilitiesByUnit, cumulativeAssets, cumulativeLiabilities)
        summaries.push(summary)
        cumulativeAssetsByUnit = summary.assetsByUnit
        cumulativeLiabilitiesByUnit = summary.liabilitiesByUnit
        cumulativeAssets = summary.assets
        cumulativeLiabilities = summary.liabilities
      }

      return summaries
    },

    overallSummary () {
      const incomeByUnit = {}
      const expensesByUnit = {}
      let income = 0
      let expenses = 0

      for (const summary of this.yearSummaries) {
        for (const [unit, amount] of Object.entries(summary.incomeByUnit || {})) {
          incomeByUnit[unit] = (incomeByUnit[unit] || 0) + amount
        }
        for (const [unit, amount] of Object.entries(summary.expensesByUnit || {})) {
          expensesByUnit[unit] = (expensesByUnit[unit] || 0) + amount
        }
        income += summary.income
        expenses += summary.expenses
      }

      const lastYear = this.yearSummaries[this.yearSummaries.length - 1]
      const assetsByUnit = lastYear?.assetsByUnit || {}
      const liabilitiesByUnit = lastYear?.liabilitiesByUnit || {}
      const netWorthByUnit = lastYear?.netWorthByUnit || {}
      const assets = lastYear?.assets || 0
      const liabilities = lastYear?.liabilities || 0
      const netWorth = lastYear?.netWorth || 0

      // Calculate net cash flow by unit
      const allUnits = new Set([...Object.keys(incomeByUnit), ...Object.keys(expensesByUnit)])
      const netCashFlowByUnit = {}
      for (const unit of allUnits) {
        const inc = incomeByUnit[unit] || 0
        const exp = expensesByUnit[unit] || 0
        netCashFlowByUnit[unit] = inc - exp
      }

      // Convert liabilities to absolute values for display
      const liabilitiesByUnitAbs = {}
      for (const [unit, amount] of Object.entries(liabilitiesByUnit)) {
        liabilitiesByUnitAbs[unit] = Math.abs(amount)
      }

      return {
        incomeByUnit,
        expensesByUnit,
        netCashFlowByUnit,
        assetsByUnit,
        liabilitiesByUnit: liabilitiesByUnitAbs,
        netWorthByUnit,
        assets,
        liabilities: Math.abs(liabilities),
        income,
        expenses,
        netCashFlow: income - expenses,
        netWorth
      }
    },

    // No default currency: the trend chart is skipped entirely (see
    // makeChart) rather than blending currencies into one line.
    chartData () {
      return {
        labels: this.yearSummaries.map(s => s.year),
        income: this.yearSummaries.map(s => s.income || 0),
        expenses: this.yearSummaries.map(s => s.expenses || 0),
        netCashFlow: this.yearSummaries.map(s => s.netCashFlow || 0),
        assets: this.yearSummaries.map(s => s.assets || 0),
        liabilities: this.yearSummaries.map(s => Math.abs(s.liabilities || 0)),
        netWorth: this.yearSummaries.map(s => s.netWorth || 0)
      }
    }
  },

  watch: {
    chartData: {
      handler () {
        this.$nextTick(() => this.makeChart())
      },
      deep: true
    }
  },

  mounted () {
    this.$nextTick(() => this.makeChart())
  },

  methods: {
    formatAmount,

    zoomYear (year, accountPrefix) {
      const transactions = this.transactions.filter(t => {
        if (Math.floor(t.date / 10000) !== year) return false
        return t.getPostings().some(p => p.account.startsWith(accountPrefix))
      })
      const label = accountPrefix.replace(':', '')
      this.app.zoom(`${year} - ${label}`, transactions)
    },

    zoomCategory (year, category, accountPrefix) {
      const transactions = this.transactions.filter(t => {
        if (Math.floor(t.date / 10000) !== year) return false
        return t.getPostings().some(p =>
          p.account.startsWith(accountPrefix) && p.account.includes(category)
        )
      })
      this.app.zoom(`${year} - ${category}`, transactions)
    },

    calculateYearSummary (year, cumulativeAssetsByUnit, cumulativeLiabilitiesByUnit, cumulativeAssets = 0, cumulativeLiabilities = 0) {
      const incomeByUnit = {}
      const expensesByUnit = {}
      const assetsChangeByUnit = {}
      const liabilitiesChangeByUnit = {}
      const categoryData = {}
      let assetsChange = 0
      let liabilitiesChange = 0
      let income = 0
      let expenses = 0

      for (const t of this.transactions) {
        if (Math.floor(t.date / 10000) !== year) continue

        // Use original postings to see all account types, not just filtered ones
        const postings = t._original?.getPostings() || t.getPostings()
        for (const p of postings) {
          const unit = p.currency || 'units'
          const converted = this.hasDefaultCurrency ? convertToCurrency(p, this.currency) : null

          if (p.account.startsWith('Assets:')) {
            assetsChangeByUnit[unit] = (assetsChangeByUnit[unit] || 0) + (p.amount || 0)
            if (converted !== null) assetsChange += converted
          } else if (p.account.startsWith('Liabilities:')) {
            if (this.knownCurrencies.has(p.currency)) {
              liabilitiesChangeByUnit[unit] = (liabilitiesChangeByUnit[unit] || 0) + (p.amount || 0)
            }
            if (converted !== null) liabilitiesChange += converted
          } else if (p.account.startsWith('Income:')) {
            if (this.knownCurrencies.has(p.currency)) {
              const amt = Math.abs(p.amount || 0)
              incomeByUnit[unit] = (incomeByUnit[unit] || 0) + amt

              const category = this.extractCategory(p.account, 'Income:')
              if (!categoryData[category]) categoryData[category] = { incomeByUnit: {}, expensesByUnit: {} }
              categoryData[category].incomeByUnit[unit] = (categoryData[category].incomeByUnit[unit] || 0) + amt
            }
            if (converted !== null) income += Math.abs(converted)
          } else if (p.account.startsWith('Expenses:')) {
            if (this.knownCurrencies.has(p.currency)) {
              const amt = p.amount || 0
              expensesByUnit[unit] = (expensesByUnit[unit] || 0) + amt

              const category = this.extractCategory(p.account, 'Expenses:')
              if (!categoryData[category]) categoryData[category] = { incomeByUnit: {}, expensesByUnit: {} }
              categoryData[category].expensesByUnit[unit] = (categoryData[category].expensesByUnit[unit] || 0) + amt
            }
            if (converted !== null) expenses += converted
          }
        }
      }

      // Helper to sum values across currencies for sorting
      const sumValues = (byUnit) => Object.values(byUnit || {}).reduce((a, b) => a + b, 0)

      const categories = Object.entries(categoryData)
        .map(([name, data]) => {
          const incomeByUnit = data.incomeByUnit || {}
          const expensesByUnit = data.expensesByUnit || {}
          const allUnits = new Set([...Object.keys(incomeByUnit), ...Object.keys(expensesByUnit)])
          const netByUnit = {}
          for (const unit of allUnits) {
            netByUnit[unit] = (incomeByUnit[unit] || 0) - (expensesByUnit[unit] || 0)
          }

          return {
            name,
            incomeByUnit,
            expensesByUnit,
            netByUnit
          }
        })
        .sort((a, b) => Math.abs(sumValues(b.netByUnit)) - Math.abs(sumValues(a.netByUnit)))

      // Merge cumulative assets with changes
      const assetsByUnit = { ...cumulativeAssetsByUnit }
      for (const [unit, change] of Object.entries(assetsChangeByUnit)) {
        assetsByUnit[unit] = (assetsByUnit[unit] || 0) + change
      }

      // Merge cumulative liabilities with changes
      const liabilitiesByUnit = { ...cumulativeLiabilitiesByUnit }
      for (const [unit, change] of Object.entries(liabilitiesChangeByUnit)) {
        liabilitiesByUnit[unit] = (liabilitiesByUnit[unit] || 0) + change
      }

      // Calculate net cash flow by unit
      const allUnits = new Set([...Object.keys(incomeByUnit), ...Object.keys(expensesByUnit)])
      const netCashFlowByUnit = {}
      for (const unit of allUnits) {
        const inc = incomeByUnit[unit] || 0
        const exp = expensesByUnit[unit] || 0
        netCashFlowByUnit[unit] = inc - exp
      }

      // Calculate net worth by unit (assets + liabilities for each unit)
      const netWorthUnits = new Set([...Object.keys(assetsByUnit), ...Object.keys(liabilitiesByUnit)])
      const netWorthByUnit = {}
      for (const unit of netWorthUnits) {
        if (!this.knownCurrencies.has(unit)) continue
        const assets = assetsByUnit[unit] || 0
        const liabilities = liabilitiesByUnit[unit] || 0
        netWorthByUnit[unit] = assets + liabilities
      }

      const assets = cumulativeAssets + assetsChange
      const liabilities = cumulativeLiabilities + liabilitiesChange

      return {
        year,
        incomeByUnit,
        expensesByUnit,
        netCashFlowByUnit,
        assetsByUnit,
        liabilitiesByUnit,
        netWorthByUnit,
        categories,
        assets,
        liabilities,
        income,
        expenses,
        netCashFlow: income - expenses,
        netWorth: assets + liabilities
      }
    },

    extractCategory (account, prefix) {
      const withoutPrefix = account.slice(prefix.length)
      const parts = withoutPrefix.split(':')
      return parts[0] || 'Other'
    },

    makeChart () {
      if (!this.$refs.chartWrapper) return
      if (this.yearSummaries.length < 2) return

      const canvas = document.createElement('canvas')
      canvas.className = 'block mx-auto w-full h-full'

      this.$refs.chartWrapper.innerHTML = ''
      this.$refs.chartWrapper.appendChild(canvas)

      // eslint-disable-next-line no-new
      new Chart(canvas, {
        type: 'bar',
        data: {
          labels: this.chartData.labels,
          datasets: [
            {
              label: 'Income',
              data: this.chartData.income,
              backgroundColor: 'rgba(34, 197, 94, 0.7)',
              borderColor: 'rgb(34, 197, 94)',
              borderWidth: 1,
              yAxisID: 'y'
            },
            {
              label: 'Expenses',
              data: this.chartData.expenses,
              backgroundColor: 'rgba(239, 68, 68, 0.7)',
              borderColor: 'rgb(239, 68, 68)',
              borderWidth: 1,
              yAxisID: 'y'
            },
            {
              label: 'Assets',
              data: this.chartData.assets,
              type: 'line',
              borderColor: 'rgb(20, 184, 166)',
              backgroundColor: 'rgb(20, 184, 166)',
              borderWidth: 2,
              fill: false,
              tension: 0.1,
              yAxisID: 'y1'
            },
            {
              label: 'Liabilities',
              data: this.chartData.liabilities,
              type: 'line',
              borderColor: 'rgb(245, 158, 11)',
              backgroundColor: 'rgb(245, 158, 11)',
              borderWidth: 2,
              fill: false,
              tension: 0.1,
              yAxisID: 'y1'
            },
            {
              label: 'Net Worth',
              data: this.chartData.netWorth,
              type: 'line',
              borderColor: 'rgb(139, 92, 246)',
              backgroundColor: 'rgb(139, 92, 246)',
              borderWidth: 2,
              borderDash: [5, 5],
              fill: false,
              tension: 0.1,
              yAxisID: 'y1'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 0 },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: 'rgb(156, 163, 175)'
              }
            }
          },
          scales: {
            y: {
              type: 'linear',
              position: 'left',
              beginAtZero: true,
              title: {
                display: true,
                text: 'Income/Expenses',
                color: 'rgb(156, 163, 175)'
              },
              ticks: {
                color: 'rgb(156, 163, 175)'
              },
              grid: {
                color: 'rgba(156, 163, 175, 0.2)'
              }
            },
            y1: {
              type: 'linear',
              position: 'right',
              beginAtZero: true,
              title: {
                display: true,
                text: 'Assets/Liabilities',
                color: 'rgb(156, 163, 175)'
              },
              ticks: {
                color: 'rgb(156, 163, 175)'
              },
              grid: {
                drawOnChartArea: false
              }
            },
            x: {
              ticks: {
                color: 'rgb(156, 163, 175)'
              },
              grid: {
                color: 'rgba(156, 163, 175, 0.2)'
              }
            }
          }
        }
      })
    }
  }
}
</script>
